import React from 'react';

// Types
interface LLMProvider {
  enum_value: string;
  name: string;
  api_key_label?: string;
  endpoints: Endpoint[];
  models: Model[];
}

interface Endpoint {
  name: string;
  path: string;
  description?: string;
  is_default?: boolean;
  endpoint_parameters?: EndpointParameter[];
}

interface EndpointParameter {
  name: string;
  label: string;
  type: string;
  description?: string;
  default?: any;
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
  applies_to_models?: string[];
}

interface Model {
  name: string;
  value: string;
  model_parameters?: ModelParameter[];
}

interface ModelParameter {
  name: string;
  label: string;
  type: string;
  description?: string;
  default?: any;
  min?: number;
  max?: number;
  step?: number;
}

interface Secret {
  id: string;
  name: string;
}

interface FormData {
  listen_address: string;
  route: string;
  provider: string;
  endpoint_url: string;
  model: string;
  api_key_secret_id: string;
  model_parameters: Record<string, any>;
  override_policy?: Record<string, string>;
}

interface LLMConfigTabProps {
  formData: FormData;
  handleInputChange: (field: string, value: any) => void;
  llmProviders: LLMProvider[];
  selectedProvider: LLMProvider | null;
  selectedModel: Model | null;
  selectedEndpoint: Endpoint | null;
  secrets: Secret[];
  setIsModified: (value: boolean) => void;
}

const LLMConfigTab: React.FC<LLMConfigTabProps> = ({
  formData,
  handleInputChange,
  llmProviders,
  selectedProvider,
  selectedModel,
  selectedEndpoint,
  secrets,
  setIsModified
}) => {
  return (
    <>
      {/* Listener Info Box */}
      {formData.listen_address && formData.route && (
        <div className="alert alert-success mb-3" style={{ fontSize: '14px' }}>
          <i className="fas fa-info-circle mr-2"></i><strong>Pipe Listener:</strong> {formData.listen_address}{formData.route}
          <button
            type="button"
            className="btn btn-xs ml-2"
            style={{ padding: '2px 6px', fontSize: '11px', outline: 'none' }}
            onClick={(e) => {
              navigator.clipboard.writeText(`${formData.listen_address}${formData.route}`);
              const btn = e.currentTarget as HTMLButtonElement;
              const originalHtml = btn.innerHTML;
              btn.innerHTML = '<i class="fas fa-check"></i>';
              setTimeout(() => { btn.innerHTML = originalHtml; }, 2000);
            }}
            title="Copy to clipboard"
          >
            <i className="fas fa-copy"></i>
          </button>
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-brain"></i> LLM Configuration
            </h6>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>Provider *</label>
                <select
                  className="form-control dropdown-styling"
                  value={formData.provider}
                  onChange={(e) => handleInputChange('provider', e.target.value)}
                  required
                >
                  {llmProviders.map(provider => (
                    <option key={provider.enum_value} value={provider.enum_value}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label>Endpoint *</label>
                <select
                  className="form-control dropdown-styling"
                  value={formData.endpoint_url}
                  onChange={(e) => handleInputChange('endpoint_url', e.target.value)}
                  required
                  disabled={!selectedProvider}
                >
                  <option value="">Select an endpoint...</option>
                  {selectedProvider?.endpoints.map((ep, idx) => (
                    <option key={idx} value={ep.path}>
                      {ep.name} {ep.is_default && '(Default)'}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  {selectedEndpoint?.description || 'Select an API endpoint'}
                </small>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>Model *</label>
                <select
                  className="form-control dropdown-styling"
                  value={formData.model}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  required
                  disabled={!selectedProvider}
                >
                  <option value="">Select a model...</option>
                  {selectedProvider?.models.map(model => (
                    <option key={model.value} value={model.value}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label>Model Policy</label>
                <select
                  className="form-control dropdown-styling"
                  value={formData.override_policy?.['model'] || 'inject_if_missing'}
                  onChange={(e) => {
                    const newPolicy = { ...formData.override_policy };
                    newPolicy['model'] = e.target.value;
                    handleInputChange('override_policy', newPolicy);
                  }}
                >
                  <option value="inject_if_missing">Inject If Missing</option>
                  <option value="gateway_wins">Gateway Wins</option>
                  <option value="client_wins">Client Wins</option>
                </select>
                <small className="form-text text-muted">
                  {(formData.override_policy?.['model'] || 'inject_if_missing') === 'gateway_wins' && '🔒 Gateway model always used, client requests ignored'}
                  {(formData.override_policy?.['model'] || 'inject_if_missing') === 'client_wins' && '🤝 Client model preferred, gateway model as fallback'}
                  {(formData.override_policy?.['model'] || 'inject_if_missing') === 'inject_if_missing' && '➕ Use gateway model only when client omits model'}
                </small>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>{selectedProvider?.api_key_label || 'API Key Secret'} *</label>
                <select
                  className="form-control dropdown-styling"
                  value={formData.api_key_secret_id}
                  onChange={(e) => handleInputChange('api_key_secret_id', e.target.value)}
                  required
                >
                  <option value="">Select a secret...</option>
                  {secrets.map(secret => (
                    <option key={secret.id} value={secret.id}>
                      {secret.name}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  API key secret for authentication with the LLM provider
                </small>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label>API Key Policy</label>
                <select
                  className="form-control dropdown-styling"
                  value={formData.override_policy?.['api_key'] || 'gateway_wins'}
                  onChange={(e) => {
                    const newPolicy = { ...formData.override_policy };
                    newPolicy['api_key'] = e.target.value;
                    handleInputChange('override_policy', newPolicy);
                  }}
                >
                  <option value="gateway_wins">Gateway Wins</option>
                  <option value="client_wins">Client Wins</option>
                  <option value="inject_if_missing">Inject If Missing</option>
                </select>
                <small className="form-text text-muted">
                  {(formData.override_policy?.['api_key'] || 'gateway_wins') === 'gateway_wins' && '🔒 Gateway API key always used, client requests ignored'}
                  {(formData.override_policy?.['api_key'] || 'gateway_wins') === 'client_wins' && '🤝 Client API key preferred, gateway key as fallback'}
                  {(formData.override_policy?.['api_key'] || 'gateway_wins') === 'inject_if_missing' && '➕ Use gateway API key only when client omits key'}
                </small>
              </div>
            </div>
          </div>

          {/* Dynamic Endpoint Parameters */}
          {selectedEndpoint?.endpoint_parameters && selectedEndpoint.endpoint_parameters.length > 0 && (
            <>
              <hr className="my-4" />
              <h6 className="mb-3 text-primary">
                <i className="fas fa-cog mr-2"></i>
                Endpoint Parameters
              </h6>
              {(() => {
                const filteredParams = selectedEndpoint.endpoint_parameters.filter(param => {
                  // Filter by applies_to_models if specified
                  if (!param.applies_to_models || param.applies_to_models.length === 0) {
                    return true;
                  }
                  return param.applies_to_models.includes(formData.model);
                });
                
                if (filteredParams.length === 0) {
                  return (
                    <div className="alert alert-success">
                      <i className="fas fa-check-circle mr-2"></i>
                      No additional endpoint parameters required for <strong>{formData.model || 'this model'}</strong>
                    </div>
                  );
                } else {
                  return (
                    <>
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle mr-2"></i>
                        Configure endpoint-specific parameters for <strong>{selectedEndpoint.name}</strong>
                      </div>
                      <div className="row">
                        {filteredParams.map((param) => {
                          return (
                            <div key={param.name} className="col-md-6">
                              <div className="form-group">
                                <label>{param.label}</label>
                                {param.type === 'number' && (
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={formData.model_parameters[param.name] ?? param.default}
                                    onChange={(e) => {
                                      const val = e.target.value === '' ? (param.default || 0) : parseFloat(e.target.value);
                                      handleInputChange('model_parameters', {
                                        ...formData.model_parameters,
                                        [param.name]: val
                                      });
                                    }}
                                    min={param.min}
                                    max={param.max}
                                    step={param.step || 1}
                                  />
                                )}
                                {param.type === 'string' && !param.options && (
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={formData.model_parameters[param.name] ?? param.default}
                                    onChange={(e) => handleInputChange('model_parameters', {
                                      ...formData.model_parameters,
                                      [param.name]: e.target.value
                                    })}
                                  />
                                )}
                                {param.type === 'select' && param.options && (
                                  <select
                                    className="form-control dropdown-styling"
                                    value={formData.model_parameters[param.name] ?? param.default}
                                    onChange={(e) => handleInputChange('model_parameters', {
                                      ...formData.model_parameters,
                                      [param.name]: e.target.value
                                    })}
                                  >
                                    {param.options.map((option: string) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                )}
                                {(param.type === 'string' && param.options) && (
                                  <select
                                    className="form-control dropdown-styling"
                                    value={formData.model_parameters[param.name] ?? param.default}
                                    onChange={(e) => handleInputChange('model_parameters', {
                                      ...formData.model_parameters,
                                      [param.name]: e.target.value
                                    })}
                                  >
                                    {param.options.map((option: string) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                )}
                                {param.type === 'boolean' && (
                                  <div className="custom-control custom-switch">
                                    <input
                                      type="checkbox"
                                      className="custom-control-input"
                                      id={`endpoint_param_${param.name}`}
                                      checked={formData.model_parameters[param.name] ?? param.default}
                                      onChange={(e) => handleInputChange('model_parameters', {
                                        ...formData.model_parameters,
                                        [param.name]: e.target.checked
                                      })}
                                    />
                                    <label className="custom-control-label" htmlFor={`endpoint_param_${param.name}`}>
                                      Enabled
                                    </label>
                                  </div>
                                )}
                                {param.description && (
                                  <small className="form-text text-muted">
                                    {param.description}
                                    {param.type === 'number' && param.min !== undefined && param.max !== undefined && 
                                      ` (range: ${param.min} - ${param.max}, default: ${param.default})`}
                                  </small>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                }
              })()}
            </>
          )}

          {/* Dynamic Model Parameters */}
          {selectedModel?.model_parameters && selectedModel.model_parameters.length > 0 && (
            <>
              <hr className="my-4" />
              <h6 className="mb-3 text-primary">
                <i className="fas fa-sliders-h mr-2"></i>
                Model Parameters & Override Policies
              </h6>
              
              <div className="row">
                {selectedModel.model_parameters.map((param) => {
                  const isReasoningEffort = param.name === 'reasoning_effort';
                  const isO1Model = formData.model?.toLowerCase().includes('o1') || formData.model?.toLowerCase().includes('gpt-5');
                  const policyDisabled = isReasoningEffort && !isO1Model;
                  
                  return (
                    <div key={param.name} className="col-md-12">
                      <div className="form-group">
                        <label>{param.label}</label>
                        <div className="row">
                          <div className="col-md-6">
                            {param.type === 'number' && (
                              <input
                                type="number"
                                className="form-control"
                                value={formData.model_parameters[param.name] ?? param.default}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                  handleInputChange('model_parameters', {
                                    ...formData.model_parameters,
                                    [param.name]: val
                                  });
                                }}
                                min={param.min}
                                max={param.max}
                                step={param.step || 1}
                              />
                            )}
                            {param.type === 'string' && (
                              <input
                                type="text"
                                className="form-control"
                                value={formData.model_parameters[param.name] ?? param.default}
                                onChange={(e) => handleInputChange('model_parameters', {
                                  ...formData.model_parameters,
                                  [param.name]: e.target.value
                                })}
                              />
                            )}
                            {param.type === 'boolean' && (
                              <div className="custom-control custom-switch">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id={`param_${param.name}`}
                                  checked={formData.model_parameters[param.name] ?? param.default}
                                  onChange={(e) => handleInputChange('model_parameters', {
                                    ...formData.model_parameters,
                                    [param.name]: e.target.checked
                                  })}
                                />
                                <label className="custom-control-label" htmlFor={`param_${param.name}`}>
                                  Enabled
                                </label>
                              </div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <select
                              className="form-control dropdown-styling"
                              value={formData.override_policy?.[param.name] || 'inject_if_missing'}
                              disabled={policyDisabled}
                              onChange={(e) => {
                                const newPolicy = { ...formData.override_policy };
                                newPolicy[param.name] = e.target.value;
                                handleInputChange('override_policy', newPolicy);
                              }}
                            >
                              <option value="inject_if_missing">Inject If Missing</option>
                              <option value="gateway_wins">Gateway Wins</option>
                              <option value="client_wins">Client Wins</option>
                            </select>
                            {policyDisabled && (
                              <small className="form-text text-muted">Policy only applies to o1/o3/gpt-5 models</small>
                            )}
                          </div>
                        </div>
                        {param.description && (
                          <small className="form-text text-muted">
                            {param.description}
                            {param.type === 'number' && param.min !== undefined && param.max !== undefined && 
                              ` (range: ${param.min} - ${param.max}, default: ${param.default})`}
                          </small>
                        )}
                        <small className="form-text" style={{ color: '#0066cc', marginTop: '4px', display: 'block' }}>
                          {(formData.override_policy?.[param.name] || 'inject_if_missing') === 'gateway_wins' && '🔒 Gateway value always used, client SDK requests ignored'}
                          {(formData.override_policy?.[param.name] || 'inject_if_missing') === 'client_wins' && '🤝 Client SDK value preferred, gateway as fallback'}
                          {(formData.override_policy?.[param.name] || 'inject_if_missing') === 'inject_if_missing' && '➕ Inject only when client SDK omits this parameter'}
                        </small>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default LLMConfigTab;
